<template>
  <div data-test-id="site-selector-locations-list">
    <q-list
      v-if="(!!locationZones && locationZones.length) || !allSitesHaveImages || isWidget || dropdownListView"
      v-bind="$attrs"
      :bordered="bordered"
      :dense="dropdownListView"
      :class="{ 'rounded-borders q-mt-md': !dropdownListView }"
    >
      <slot name="title" />
      <div :class="{ 'row justify-between items-start': showSiteImage }">
        <site-location-zone-card
          v-for="site in sitesWithoutLocationZoneId"
          :key="site.id"
          :site="site"
          :card-color="cardColor"
          :card-color-hex="cardColorHex"
          :card-border-radius="cardBorderRadius"
          :class="{
            'bg-responsive border-top border-right border-bottom border-left rounded-borders q-ma-sm q-py-sm': !dropdownListView && !showSiteImage,
            'col-12 col-sm-6 q-mt-md q-mb-md': showSiteImage
          }"
          :style="{
            'max-width': showSiteImage && $q.screen.gt.xs ? '47%' : undefined,
            'border-radius': !dropdownListView ? `${cardBorderRadius}px` : undefined
          }"
          :hide-address="hideAddress"
          :show-distance="showDistance"
          :show-site-image="showSiteImage"
          :show-site-description="showSiteDescription"
          :flat="dropdownListView || flat"
          :navigate-on-select="navigateOnSelect"
          :redirect-home-page="redirectHomePage"
          :selected-locations="selectedLocations"
          @select="$emit('select', site)"
          @select-location="(site, action) => $emit('select-location', site, action)"
        />
      </div>
      <q-expansion-item
        v-for="zone in locationZones"
        :key="zone.id"
        v-model="expandStateByZoneId[zone.id]"
        dense
        :header-class="headerClass"
        :default-opened="defaultOpened"
        :expand-separator="dropdownListView"
      >
        <template
          #header
        >
          <q-item-section
            :class="dropdownListView ? 'text-body2 text-uppercase text-responsive-grey-9' : 'text-h6'"
          >
            {{ zone.name }}
          </q-item-section>

          <q-item-section side>
            <div class="row items-center text-subtitle2">
              {{ zoneCaption(zone) }}
            </div>
          </q-item-section>
        </template>
        <div :class="{ 'row justify-between items-start q-px-md': showSiteImage }">
          <site-location-zone-card
            v-for="site in zone.visibleSites"
            :key="site.id"
            :site="site"
            :card-color="cardColor"
            :card-color-hex="cardColorHex"
            :card-border-radius="cardBorderRadius"
            :class="{
              'bg-responsive border-top border-right border-bottom border-left rounded-borders q-ma-sm q-py-sm': !dropdownListView && !showSiteImage,
              'col-12 col-sm-6 q-mt-md q-mb-md': showSiteImage
            }"
            :style="{
              'max-width': showSiteImage && $q.screen.gt.xs ? '47%' : undefined,
              'border-radius': !dropdownListView ? `${cardBorderRadius}px` : undefined
            }"
            :hide-address="hideAddress"
            :dropdown-list-view="dropdownListView"
            :show-site-image="showSiteImage"
            :show-site-description="showSiteDescription"
            :flat="dropdownListView || flat"
            :navigate-on-select="navigateOnSelect"
            :redirect-home-page="redirectHomePage"
            :selected-locations="selectedLocations"
            @select="$emit('select', site)"
            @select-location="(site, action) => $emit('select-location', site, action)"
          />
        </div>
      </q-expansion-item>
    </q-list>
    <div
      v-else
      class="row justify-evenly q-mt-sm"
    >
      <div
        v-for="site in siteLocations"
        :key="site.id"
        :class="$q.screen.gt.sm ? 'q-ma-sm' : 'col-12 q-pa-md'"
        style="max-width: 300px;"
        :style="{
          'flex-shrink': $q.screen.gt.sm ? '1' : undefined,
          'width': $q.screen.gt.sm ? '300px' : undefined,
        }"
      >
        <site-selector-card
          class="rounded-borders"
          :site="site"
          :card-color="cardColor"
          :card-color-hex="cardColorHex"
          :card-border-radius="cardBorderRadius"
          :redirect-home-page="redirectHomePage"
          :navigate-on-select="navigateOnSelect"
          :selected-locations="selectedLocations"
          @select="$emit('select', site)"
          @select-location="(site, action) => $emit('select-location', site, action)"
        />
      </div>
    </div>
    <slot name="action" />
  </div>
</template>

<script>
import SiteLocationZoneCard from 'src/modules/consumer/components/site-selector/SiteLocationZoneCard'
import SiteSelectorCard from 'src/modules/consumer/components/site-selector/SiteSelectorCard'

export default {
  name: 'SiteSelectorLocationsList',
  components: {
    SiteLocationZoneCard,
    SiteSelectorCard
  },
  props: {
    sitesWithoutLocationZoneId: {
      type: Array,
      required: false,
      default: () => []
    },
    locationZones: {
      type: Array,
      required: false,
      default: () => []
    },
    siteLocations: {
      type: Array,
      required: false,
      default: () => []
    },
    hideAddress: {
      type: Boolean,
      required: false,
      default: false
    },
    dropdownListView: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultOpened: {
      type: Boolean,
      required: false,
      default: false
    },
    showDistance: {
      type: Boolean,
      required: false,
      default: false
    },
    allSitesHaveImages: {
      type: Boolean,
      required: false,
      default: false
    },
    cardColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    cardColorHex: {
      type: String,
      required: false,
      default: '#ffffff'
    },
    cardBorderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    showSiteImage: {
      type: Boolean,
      required: false,
      default: false
    },
    showSiteDescription: {
      type: Boolean,
      required: false,
      default: false
    },
    redirectHomePage: {
      type: Boolean,
      required: false,
      default: false
    },
    bordered: {
      type: Boolean,
      required: false,
      default: false
    },
    isWidget: {
      type: Boolean,
      required: false,
      default: false
    },
    flat: {
      type: Boolean,
      required: false,
      default: false
    },
    navigateOnSelect: {
      type: Boolean,
      required: false,
      default: true
    },
    selectedLocations: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: ['select', 'select-location'],
  data () {
    return {
      expandStateByZoneId: {}
    }
  },
  computed: {
    headerClass () {
      return this.dropdownListView ? 'expansion-header text-responsive text-left' : 'expansion-header text-responsive text-left q-pa-md'
    }
  },
  watch: {
    locationZones: {
      immediate: true,
      handler (newZones) {
        this.expandStateByZoneId = _.reduce(newZones, (acc, zone) => {
          acc[zone.id] = this.defaultOpened
          return acc
        }, {})
      }
    },
    defaultOpened: {
      handler (newVal) {
        this.expandStateByZoneId = _.reduce(this.locationZones, (acc, zone) => {
          acc[zone.id] = newVal
          return acc
        }, {})
      }
    }
  },
  methods: {
    zoneCaption (locationZone) {
      return `${locationZone.visibleSites.length} ${this.$tc('siteLocation.location', locationZone.visibleSites.length)}`
    }
  }
}
</script>
